/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import {
  useApiClient,
  ZAvatar,
  useFetchImmutable,
} from '@marageti/z4-lib';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { useSnackbar } from 'notistack';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  getCountryOfResidence, getPhoneNumber, getTimeZone,
} from '../../../utils/helpers';

type CoTravelerViewProps = {
  onClose: () => void;
  tripRequest: TripRequest;
  coTravelerId: string;
  onSuccess?: () => void;
};

const CoTravelerView = ({
  onClose, tripRequest, coTravelerId, onSuccess,
}: CoTravelerViewProps) => {
  const [isRemovingCoTraveler, setIsRemovingCoTraveler] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);

  const isPrimaryTraveler = tripRequest.travelers[0].id === coTravelerId;

  const coTraveler = tripRequest.travelers.find((traveler) => traveler.id === coTravelerId);
  const name = coTraveler?.fullName;
  const title = isPrimaryTraveler ? 'Primary Traveler' : 'Co-Traveler';

  const onConfirmRemoveCoTraveler = async () => {
    if (!tripRequest.id) return;

    setIsLoading(true);
    try {
      await apiClient.tripRequestsClient.deleteTripRequestTraveler(tripRequest.id, coTravelerId);
      enqueueSnackbar('Co-Traveler Removed.', { variant: 'default' });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error removing co-traveler:', error);
      enqueueSnackbar('Error removing co-traveler', { variant: 'default' });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const onRemoveCoTraveler = async () => {
    setIsRemovingCoTraveler(true);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ paddingBottom: 0 }}>
        <Stack direction="column" spacing={2} alignItems="center">
          <ZAvatar size={128} src={coTraveler?.avatar} name={coTraveler?.fullName} />
          <Stack direction="column" spacing={0} alignItems="center">
            <Typography variant="h3">{name}</Typography>
            <Typography variant="body">{title}</Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 0 }}>
        <Stack spacing={2} sx={{ pt: 4 }}>
          <Stack>
            <Typography variant="captionBold">Email</Typography>
            <Typography variant="body">{coTraveler?.email}</Typography>
          </Stack>
          <Stack>
            <Typography variant="captionBold">Phone</Typography>
            <Typography variant="body">{getPhoneNumber(coTraveler)}</Typography>
          </Stack>
          <Stack>
            <Typography variant="captionBold">Country of Residence</Typography>
            <Typography variant="body">{countryCodes?.[getCountryOfResidence(coTraveler) || '']}</Typography>
          </Stack>
          <Stack>
            <Typography variant="captionBold">Timezone</Typography>
            <Typography variant="body">{getTimeZone(coTraveler)}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        {!isPrimaryTraveler && (
          <Button
            variant="contained"
            color="error"
            onClick={onRemoveCoTraveler}
          >
            Remove Co-Traveler
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} sx={{ borderColor: '#C2C5C5' }} fullWidth={isPrimaryTraveler}>Close</Button>
      </DialogActions>
      <Dialog open={isRemovingCoTraveler} onClose={() => setIsRemovingCoTraveler(false)} maxWidth="xs">
        <DialogTitle>
          Remove
          {' '}
          {name}
        </DialogTitle>
        <DialogContent>
          <Typography>This Co-Traveler will no longer have access to this trip&apos;s information, documents, messages, or receive notifications.</Typography>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={onConfirmRemoveCoTraveler}
            disabled={isLoading}
            loading={isLoading}
          >
            Remove
          </LoadingButton>
          <Button
            variant="outlined"
            onClick={() => setIsRemovingCoTraveler(false)}
            sx={{ borderColor: '#C2C5C5' }}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CoTravelerView;
